import { Section } from '#util/pagination/helpers'

type BlockLine = Section[number]

const linesBeforeCache = new WeakMap<Section[], Map<string, number>>()
const linesAfterCache = new WeakMap<Section[], Map<string, number>>()
const linesBetweenCache = new WeakMap<Section[], Map<string, number>>()

function cached(
  store: WeakMap<Section[], Map<string, number>>,
  sections: Section[],
  key: string,
  compute: () => number,
): number {
  let inner = store.get(sections)
  if (!inner) {
    inner = new Map()
    store.set(sections, inner)
  }
  if (inner.has(key)) return inner.get(key)!
  const value = compute()
  inner.set(key, value)
  return value
}

export function linesBefore(
  contentOffset: number,
  sections: Section[],
  lineSpanPerWrappedLine: number,
): number {
  return cached(linesBeforeCache, sections, `${contentOffset}`, () => {
    let sectionStart = 0
    let lineCount = 0

    for (const section of sections) {
      if (contentOffset <= sectionStart) break
      const offsetInSection = contentOffset - sectionStart
      lineCount += firstLineStartingAtOrAfter(section, offsetInSection)
      const sectionLen = section.at(-1)?.to ?? 0
      sectionStart += sectionLen + 1
    }

    return lineCount * lineSpanPerWrappedLine
  })
}

export function linesAfter(
  contentOffset: number,
  sections: Section[],
  lineSpanPerWrappedLine: number,
): number {
  return cached(linesAfterCache, sections, `${contentOffset}`, () => {
    let sectionStart = 0
    let lineCount = 0

    for (const section of sections) {
      const offsetInSection = contentOffset - sectionStart
      lineCount +=
        section.length - firstLineEndingAfter(section, offsetInSection)
      const sectionLen = section.at(-1)?.to ?? 0
      sectionStart += sectionLen + 1
    }

    return lineCount * lineSpanPerWrappedLine
  })
}

function firstLineStartingAtOrAfter(
  section: Section,
  offsetInSection: number,
): number {
  return lowerBound(section, (line) => line.from >= offsetInSection)
}

function firstLineEndingAfter(
  section: Section,
  offsetInSection: number,
): number {
  return lowerBound(section, (line) => line.to > offsetInSection)
}

function lowerBound(
  section: Section,
  isAtOrAfterTarget: (line: BlockLine) => boolean,
): number {
  let lo = 0
  let hi = section.length

  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2)
    if (isAtOrAfterTarget(section[mid])) {
      hi = mid
    } else {
      lo = mid + 1
    }
  }

  return lo
}

export function linesBetween(
  fromOffset: number,
  toOffset: number,
  sections: Section[],
  lineSpanPerWrappedLine: number,
): number {
  if (toOffset <= fromOffset) return 0

  return cached(
    linesBetweenCache,
    sections,
    `${fromOffset}:${toOffset}`,
    () => {
      const totalLines = sections.reduce(
        (sum, section) => sum + section.length,
        0,
      )
      const lineCount =
        linesBefore(toOffset, sections, 1) +
        linesAfter(fromOffset, sections, 1) -
        totalLines

      return lineCount * lineSpanPerWrappedLine
    },
  )
}
